import { PageLayout } from '@/shared/components/PageLayout'
import { Link } from 'react-router-dom'
import { PAGE_URLS, SERVICE_IDS } from '@/lib/constants'
import { content } from '@/lib/content'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Heart,
  Building2,
  Gift,
  PartyPopper,
  HandHeart,
  Users,
  Briefcase,
  Phone,
  ArrowRight,
  Sparkles
} from 'lucide-react'

const serviceData = [
  {
    id: SERVICE_IDS.WEDDING_RECEPTIONS,
    icon: Heart,
    title: content.services.items.weddingReceptions.heading,
    image: content.services.items.weddingReceptions.image,
    paragraphs: content.services.items.weddingReceptions.paragraphs,
    hasGalleryLink: true,
  },
  {
    id: SERVICE_IDS.CORPORATE_FUNCTIONS,
    icon: Building2,
    title: content.services.items.corporateFunctions.heading,
    image: content.services.items.corporateFunctions.image,
    paragraphs: content.services.items.corporateFunctions.paragraphs,
  },
  {
    id: SERVICE_IDS.HAPPIER_HOLIDAYS,
    icon: Gift,
    title: content.services.items.happierHolidays.heading,
    image: content.services.items.happierHolidays.image,
    description: content.services.items.happierHolidays.description,
  },
  {
    id: SERVICE_IDS.BIRTHDAY_PARTIES,
    icon: PartyPopper,
    title: content.services.items.birthdayParties.heading,
    image: content.services.items.birthdayParties.image,
    description: content.services.items.birthdayParties.description,
  },
  {
    id: SERVICE_IDS.FUNERAL_SERVICES,
    icon: HandHeart,
    title: content.services.items.funeralServices.heading,
    image: content.services.items.funeralServices.image,
    paragraphs: content.services.items.funeralServices.paragraphs,
  },
  {
    id: SERVICE_IDS.FUNDRAISERS,
    icon: Users,
    title: content.services.items.fundraisers.heading,
    image: content.services.items.fundraisers.image,
    description: content.services.items.fundraisers.description,
  },
]

function ServiceSection({ service, index, isReversed }) {
  const IconComponent = service.icon

  return (
    <section
      id={service.id}
      className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
        isReversed ? 'lg:flex-row-reverse' : ''
      }`}
    >
      {/* Image */}
      <div className={`relative group ${isReversed ? 'lg:order-2' : ''}`}>
        <Card className="overflow-hidden border-border bg-card">
          <div className="relative h-72 md:h-80 lg:h-96 overflow-hidden">
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />

            {/* Number indicator */}
            <div className="absolute top-4 left-4">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold">{index + 1}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Decorative element */}
        <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-primary/20 rounded-lg -z-10 hidden lg:block" />
      </div>

      {/* Content */}
      <div className={isReversed ? 'lg:order-1' : ''}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <IconComponent className="h-6 w-6 text-primary" />
          </div>
          <Badge variant="outline" className="border-primary/50 text-primary">
            Service
          </Badge>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          {service.title}
        </h2>

        <Separator className="w-12 bg-primary mb-6" />

        <div className="space-y-4 text-muted-foreground leading-relaxed">
          {service.paragraphs ? (
            service.paragraphs.map((paragraph, idx) => (
              <p key={idx}>
                {service.hasGalleryLink && idx === 1 ? (
                  <>
                    See the{' '}
                    <Link
                      to={PAGE_URLS.GALLERY}
                      className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors"
                    >
                      gallery
                    </Link>{' '}
                    to see what we've done in the past!
                  </>
                ) : (
                  paragraph
                )}
              </p>
            ))
          ) : (
            <p>{service.description}</p>
          )}
        </div>

        <div className="mt-6">
          <Link to={PAGE_URLS.CONTACT}>
            <Button variant="outline" className="cursor-pointer">
              Inquire About This Service
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  )
}

export default function ServicesPage() {
  return (
    <PageLayout
      title={content.services.seo.title}
      description={content.services.seo.description}
    >
      {/* Hero Header */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 pattern-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />

        <div className="relative content-container text-center">
          <Badge variant="outline" className="mb-6 border-primary/50 bg-primary/10 text-primary px-4 py-1.5">
            <Briefcase className="mr-2 h-3.5 w-3.5" />
            What We Offer
          </Badge>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            {content.services.heading}
          </h1>

          <Separator className="w-16 bg-primary mx-auto mb-6" />

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {content.services.description}
          </p>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-8 bg-secondary/30 sticky top-20 z-40 backdrop-blur-sm">
        <div className="content-container">
          <div className="flex flex-wrap justify-center gap-2">
            {serviceData.map((service) => {
              const IconComponent = service.icon
              return (
                <a
                  key={service.id}
                  href={`#${service.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all"
                >
                  <IconComponent className="h-4 w-4 text-primary" />
                  <span className="hidden sm:inline">{service.title}</span>
                </a>
              )
            })}
          </div>
        </div>
      </section>

      {/* Services Sections */}
      <section className="section-padding">
        <div className="content-container">
          <div className="space-y-24 lg:space-y-32">
            {serviceData.map((service, index) => (
              <ServiceSection
                key={service.id}
                service={service}
                index={index}
                isReversed={index % 2 === 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-secondary/50" />
        <div className="absolute inset-0 pattern-bg opacity-50" />

        <div className="relative content-container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-8 rounded-full bg-primary/20 flex items-center justify-center warm-glow">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Let's Plan Your Event Together
            </h2>

            <p className="text-lg text-muted-foreground mb-10 max-w-xl mx-auto">
              Whether it's a wedding, corporate event, or celebration, our team is ready to help make your vision a reality.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to={PAGE_URLS.CONTACT}>
                <Button size="lg" className="h-14 px-10 cursor-pointer">
                  <Phone className="mr-2 h-5 w-5" />
                  Contact Us Today
                </Button>
              </Link>
              <Link to={PAGE_URLS.FACILITIES}>
                <Button size="lg" variant="outline" className="h-14 px-10 cursor-pointer">
                  View Our Facilities
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}
